// import { addYears } from 'date-fns';

import { RuleProps } from 'types/application';
import { idNoValidation } from 'features/applicationPage/helpers';

export const SECT_TYPE = 'applicant-type-section';
export const SECT_FILER = 'filer-details';
export const SECT_APPLICANT = 'applicant-details';

export const GENERAL_FIELDS = [
    {
        sectionID: SECT_TYPE,
        sectionName: '',
        fieldList: [
            {
                id: 'applicant-type',
                label: 'I am applying:',
                labelNote:
                    'Please ensure that the name of the filer and applicant (based on NRIC/FIN) should be the same in the Arts Entertainment Classification (AEC) and the Public Entertainment Licence (PEL).',
                type: 'radio',
                row: true,
                span: 12,
                rule: {
                    required: true,
                    disabled: ({ isOnEdit }: RuleProps) => {
                        return isOnEdit;
                    }
                },
                options: [
                    {
                        optID: 'Applicant',
                        optLabel: 'As an Applicant'
                    },
                    {
                        optID: 'Filer',
                        optLabel: 'On behalf of an Applicant'
                    }
                ],
                defaultValue: 'Applicant',
                autoFillCallback: ({ baseValues, currentValue, myInfo }: RuleProps) => {
                    if (!myInfo) return { ...baseValues };
                    const filer = [
                        `${SECT_FILER}-id-type`,
                        `${SECT_FILER}-idno`,
                        `${SECT_FILER}-salutaion`,
                        `${SECT_FILER}-name`,
                        // `${SECT_FILER}-mobile-no-country-code`,
                        `${SECT_FILER}-mobile-no-main`,
                        `${SECT_FILER}-email`
                    ];

                    const applicant = [
                        `${SECT_APPLICANT}-id-type`,
                        `${SECT_APPLICANT}-idno`,
                        `${SECT_APPLICANT}-salutaion`,
                        `${SECT_APPLICANT}-name`,
                        // `${SECT_APPLICANT}-bday`,
                        `${SECT_APPLICANT}-nationality`,
                        // `${SECT_APPLICANT}-mobile-no-country-code`,
                        `${SECT_APPLICANT}-mobile-no-main`,
                        `${SECT_APPLICANT}-email`,
                        `${SECT_APPLICANT}-uen-no`,
                        `${SECT_APPLICANT}-company`,
                        `${SECT_APPLICANT}-postal`,
                        `${SECT_APPLICANT}-block-house`,
                        `${SECT_APPLICANT}-street`,
                        `${SECT_APPLICANT}-floor`,
                        `${SECT_APPLICANT}-unit`,
                        `${SECT_APPLICANT}-building`
                    ];
                    const values = { ...baseValues };
                    values[`${SECT_TYPE}-applicant-type`] = currentValue || '';
                    if (currentValue === 'Applicant') {
                        filer.forEach((key: string) => {
                            values[key] = '';
                        });

                        applicant.forEach((key: string, idx: number) => {
                            if ([1, 3].includes(idx)) {
                                values[key] = myInfo[key] || '';
                            } else {
                                values[key] = baseValues?.[key] || myInfo[key] || '';
                            }
                        });

                        return values;
                    }

                    filer.forEach((key: string, idx: number) => {
                        if ([1, 3].includes(idx)) {
                            values[key] = myInfo[key] || '';
                        } else {
                            values[key] = baseValues?.[key] || myInfo[key] || '';
                        }
                    });

                    applicant.forEach((key: string) => {
                        if (![`${SECT_APPLICANT}-uen-no`, `${SECT_APPLICANT}-company`].includes(key)) values[key] = '';
                    });

                    return values;
                }
            }
        ]
    },
    {
        sectionID: SECT_FILER,
        sectionName: 'Filer Details',
        fieldList: [
            {
                id: 'id-type',
                apiID: 'IDType',
                label: 'ID Type',
                type: 'dropdown',
                rule: {
                    required: true,
                    baseIds: [`${SECT_TYPE}-applicant-type`],
                    disabled: ({ baseValues, isSingpass, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Filer') && isSingpass && myInfoAutoFill;
                    }
                }
                // defaultValue: 'NRC'
            },
            {
                id: 'idno',
                label: 'ID No.',
                type: 'text',
                rule: {
                    required: true,
                    baseIds: [`${SECT_FILER}-id-type`, `${SECT_TYPE}-applicant-type`],
                    customValidate: ({ currentValue, baseValues }: RuleProps) => {
                        const idType = baseValues && (baseValues[`${SECT_FILER}-id-type`] as string);
                        const result = idNoValidation(currentValue as string, idType);
                        return result;
                    },
                    disabled: ({ baseValues, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Filer') && myInfoAutoFill;
                    }
                }
            },
            {
                id: 'salutaion',
                apiID: 'Salutation',
                label: 'Salutation',
                type: 'dropdown',
                rule: { required: true },
                options: [{ optID: 'Dr', optLabel: 'Dr' }]
            },
            {
                id: 'name',
                label: 'Name',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_TYPE}-applicant-type`],
                    required: true,
                    charLength: 66,
                    disabled: ({ baseValues, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Filer') && myInfoAutoFill;
                    }
                }
            },
            {
                id: 'mobile-no',
                label: 'Mobile No.',
                type: 'combo',
                rule: { required: true },
                preField: {
                    id: 'country-code',
                    label: 'Coutry Code',
                    apiID: 'MobileNoCountryCode',
                    type: 'dropdown',
                    options: [{ optID: '65', optLabel: '+65' }],
                    defaultValue: '65'
                },
                mainField: {
                    id: 'main',
                    type: 'number',
                    rule: {
                        baseIds: [`${SECT_FILER}-mobile-no-country-code`],
                        required: true,
                        pattern: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_FILER}-mobile-no-country-code`] === '65');
                            return isSG ? /^[89][0-9]{7}$/ : undefined;
                        },
                        charLength: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_FILER}-mobile-no-country-code`] === '65');
                            return isSG ? 8 : 12;
                        }
                    }
                }
            },
            {
                id: 'office-no',
                label: 'Office No.',
                type: 'combo',
                preField: {
                    id: 'country-code',
                    label: 'Coutry Code',
                    apiID: 'OfficeNoCountryCode',
                    type: 'dropdown',
                    options: [{ optID: '65', optLabel: '+65' }],
                    defaultValue: '65',
                    rule: { disabled: true }
                },
                mainField: {
                    id: 'main',
                    type: 'number',
                    rule: {
                        baseIds: [`${SECT_FILER}-office-no-country-code`],
                        pattern: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_FILER}-office-no-country-code`] === '65');
                            return isSG ? /^[3689][0-9]{7}$/ : undefined;
                        },
                        charLength: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_FILER}-office-no-country-code`] === '65');
                            return isSG ? 8 : 12;
                        }
                    }
                },
                rule: {
                    hidden: ({ isSingpass }: RuleProps) => isSingpass
                }
            },
            {
                id: 'email',
                label: 'Email Address',
                type: 'email',
                rule: { required: true, charLength: 255 }
            }
        ],
        rule: {
            baseIds: [`${SECT_TYPE}-applicant-type`],
            hidden: ({ baseValues }: RuleProps) => {
                const result = Boolean(baseValues && baseValues[`${SECT_TYPE}-applicant-type`] === 'Applicant');
                return result;
            }
        }
    },
    {
        sectionID: SECT_APPLICANT,
        sectionName: 'Applicant Details',
        fieldList: [
            {
                id: 'id-type',
                apiID: 'IDType',
                label: 'ID Type',
                type: 'dropdown',
                // defaultValue: 'NRC',
                // rules: [
                //     {
                //         baseField: `${SECT_APPLICANT}-nationality`,
                //         ifList: ['SG'],
                //         setValue: () => 'NRC',
                //         valueIsBased: true
                //     }
                // ],
                rule: {
                    baseIds: [`${SECT_APPLICANT}-nationality`, `${SECT_TYPE}-applicant-type`],
                    required: true,
                    revalidate: [`${SECT_APPLICANT}-nationality`],
                    // setValue: ({ baseValues }: RuleProps) => {
                    //     if (baseValues && baseValues[`${SECT_APPLICANT}-nationality`] === 'SG') {
                    //         return 'NRC';
                    //     }
                    //     return '';
                    // },
                    disabled: ({ baseValues, isSingpass, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Applicant') && isSingpass && myInfoAutoFill;
                    }
                }
            },
            {
                id: 'idno',
                label: 'ID No.',
                type: 'text',
                // defaultValue: 'S6005048A',
                // rule: [{ required: true, charLength: 9 }],
                rule: {
                    baseIds: [`${SECT_APPLICANT}-id-type`, `${SECT_TYPE}-applicant-type`],
                    required: true,
                    customValidate: ({ currentValue, baseValues }: RuleProps) => {
                        const idType = baseValues && (baseValues[`${SECT_APPLICANT}-id-type`] as string);
                        const result = idNoValidation(currentValue as string, idType);
                        return result;
                    },
                    disabled: ({ baseValues, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Applicant') && myInfoAutoFill;
                    }
                }
            },
            {
                id: 'salutaion',
                apiID: 'Salutation',
                label: 'Salutation',
                type: 'dropdown',
                rule: { required: true },
                // defaultValue: 'Mr',
                options: [{ optID: 'Dr', optLabel: 'Dr' }]
            },
            {
                id: 'name',
                label: 'Name',
                type: 'text',
                // defaultValue: 'Alex Sim',
                rule: {
                    baseIds: [`${SECT_TYPE}-applicant-type`],
                    required: true,
                    charLength: 66,
                    disabled: ({ baseValues, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Applicant') && myInfoAutoFill;
                    }
                }
            },
            // {
            //     id: 'bday',
            //     label: 'Date of Birth',
            //     type: 'date',
            //     rule: {
            //         dateLimit: { maxDate: addYears(new Date(), -18) },
            //         customValidate: ({ currentValue }: RuleProps) => {
            //             if (currentValue && new Date(currentValue as string) > addYears(new Date(), -18)) {
            //                 return 'Allowed Age is 18 and above.';
            //             }
            //             return true;
            //         }
            //     }
            // },
            {
                id: 'nationality',
                apiID: 'Nationality',
                label: 'Nationality',
                type: 'dropdown',
                // rules: [
                //     {
                //         baseField: `${SECT_APPLICANT}-id-type`,
                //         ifList: ['NRC'],
                //         setValue: () => 'SG',
                //         valueIsBased: true
                //     }
                // ],
                rule: {
                    baseIds: [`${SECT_APPLICANT}-id-type`, `${SECT_TYPE}-applicant-type`],
                    required: true,
                    // setValue: ({ baseValues }: RuleProps) => {
                    //     if (baseValues && baseValues[`${SECT_APPLICANT}-id-type`] === 'NRC') {
                    //         return 'SG';
                    //     }
                    //     return '';
                    // },
                    customValidate: ({ currentValue, baseValues }: RuleProps) => {
                        const idType = baseValues && (baseValues[`${SECT_APPLICANT}-id-type`] as string);
                        const valid = currentValue === 'SG' && idType === 'NRC';
                        const validOthers = currentValue !== 'SG' && idType !== 'NRC';
                        const result = valid || validOthers || 'Invalid Nationality for Selected ID Type';
                        return result;
                    },
                    disabled: ({ baseValues, isSingpass, myInfoAutoFill }: RuleProps) => {
                        return Boolean(baseValues?.[`${SECT_TYPE}-applicant-type`] === 'Applicant') && isSingpass && myInfoAutoFill;
                    }
                }
            },
            {
                id: 'mobile-no',
                label: 'Mobile No.',
                type: 'combo',
                rule: { required: true },
                preField: {
                    id: 'country-code',
                    label: 'Coutry Code',
                    apiID: 'MobileNoCountryCode',
                    type: 'dropdown',
                    options: [{ optID: '65', optLabel: '+65' }],
                    defaultValue: '65'
                },
                mainField: {
                    id: 'main',
                    type: 'number',
                    rule: {
                        baseIds: [`${SECT_APPLICANT}-mobile-no-country-code`],
                        required: true,
                        pattern: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_APPLICANT}-mobile-no-country-code`] === '65');
                            return isSG ? /^[89][0-9]{7}$/ : undefined;
                        },
                        charLength: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_APPLICANT}-mobile-no-country-code`] === '65');
                            return isSG ? 8 : 12;
                        }
                    }
                }
            },
            {
                id: 'office-no',
                label: 'Office No.',
                type: 'combo',
                preField: {
                    id: 'country-code',
                    label: 'Coutry Code',
                    apiID: 'OfficeNoCountryCode',
                    type: 'dropdown',
                    options: [{ optID: '65', optLabel: '+65' }],
                    defaultValue: '65',
                    rule: { disabled: true }
                },
                mainField: {
                    id: 'main',
                    type: 'number',
                    rule: {
                        baseIds: [`${SECT_APPLICANT}-office-no-country-code`],
                        pattern: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_APPLICANT}-office-no-country-code`] === '65');
                            return isSG ? /^[3689][0-9]{7}$/ : undefined;
                        },
                        charLength: ({ baseValues }: RuleProps) => {
                            const isSG = Boolean(baseValues && baseValues[`${SECT_APPLICANT}-office-no-country-code`] === '65');
                            return isSG ? 8 : 12;
                        }
                    }
                },
                rule: {
                    hidden: ({ isSingpass }: RuleProps) => isSingpass
                }
            },
            {
                id: 'email',
                label: 'Email Address',
                type: 'email',
                // defaultValue: 'alex.a.sim@accenture.com',
                rule: { required: true, charLength: 255 }
                // isNextEmpty: true
            },
            {
                id: 'company',
                label: 'Name of Company',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_TYPE}-applicant-type`],
                    charLength: 100,
                    hidden: ({ isSingpass }: RuleProps) => isSingpass,
                    disabled: ({ myInfoAutoFill }: RuleProps) => {
                        return myInfoAutoFill;
                    }
                }
            },
            {
                id: 'uen-no',
                label: 'UEN No.',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_TYPE}-applicant-type`],
                    charLength: 10,
                    hidden: ({ isSingpass }: RuleProps) => isSingpass,
                    disabled: true
                }
            },
            {
                id: 'empty-1',
                label: '',
                type: '',
                rule: {
                    hidden: ({ isSingpass }: RuleProps) => !isSingpass
                }
            },
            {
                id: 'address-type',
                label: 'Address Type',
                apiID: 'AddressType',
                type: 'dropdown',
                options: [{ optID: 'SG', optLabel: 'Singapore' }],
                defaultValue: 'SG',
                rule: {
                    required: true,
                    disabled: true
                },
                isNextEmpty: true
            },
            {
                id: 'postal',
                label: 'Postal Code',
                type: 'postal',
                rule: { required: true }
            },
            {
                id: 'block-house',
                label: 'Block/House No.',
                type: 'dropdown',
                rule: { required: true, charLength: 10 }
            },
            {
                id: 'street',
                label: 'Street Name',
                type: 'text',
                rule: {
                    baseIds: [`${SECT_APPLICANT}-postal`],
                    required: true,
                    charLength: 500
                }
            },
            {
                id: 'floor',
                label: 'Floor No.',
                type: 'text',
                placeholder: 'Eg: 01, B1, 01M',
                span: 2,
                rule: { charLength: 3 }
            },
            {
                id: 'unit',
                label: 'Unit No.',
                type: 'text',
                placeholder: 'Eg: #05-01, use 01',
                span: 2,
                rule: { charLength: 5 }
            },
            {
                id: 'building',
                label: 'Building Name',
                type: 'text',
                rule: { charLength: 500 }
            }
        ]
    }
];
